import { LayoutTemplate, LockKeyhole, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BookExportBookMetadataForm } from "@/components/book-assembly/book-export-book-metadata-form";
import { BookExportSummaryCard } from "@/components/book-assembly/book-export-summary-card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  BookExportCapabilityMatrix,
  BookExportConfig,
  BookExportPageSize,
  BookExportPreset,
  BookExportSettings,
  BookExportSummary,
  BookExportTarget,
} from "@/features/book-assembly/book-assembly.types";
import {
  bookExportModes,
  bookExportPageSizes,
  bookExportPresets,
  bookExportTargets,
} from "@/features/book-assembly/book-assembly.types";

type ExportSettingsStepProps = {
  capabilities: BookExportCapabilityMatrix;
  config: BookExportConfig;
  defaultAuthorName: string;
  defaultBookTitle: string;
  metadataStatus: "error" | "idle" | "ready" | "saving";
  onConfigChange: (patch: Partial<BookExportConfig>) => void;
  onSaveBookMetadata: () => void;
  onSettingsChange: (patch: Partial<BookExportSettings>) => void;
  settings: BookExportSettings;
  summary: BookExportSummary | null | undefined;
};

function getTargetLabelKey(target: BookExportTarget) {
  return `bookAssembly.summary.targets.${target}`;
}

function getModeLabelKey(mode: BookExportConfig["exportMode"]) {
  return `bookAssembly.summary.modes.${mode}`;
}

function getPresetLabelKey(preset: BookExportPreset) {
  return `bookAssembly.summary.presets.${preset}`;
}

function getPageSizeLabelKey(pageSize: BookExportPageSize) {
  return `bookAssembly.summary.pageSizes.${pageSize}`;
}

function getFrontMatterSourceLabelKey(source: BookExportConfig["frontMatterMessageSource"]) {
  return `bookAssembly.exportSettings.frontMatterSources.${source}`;
}

function getEndMatterSourceLabelKey(source: BookExportConfig["endMatterMessageSource"]) {
  return `bookAssembly.exportSettings.endMatterSources.${source}`;
}

function getTierLabelKey(tier: BookExportCapabilityMatrix["tier"]) {
  return `common.tiers.${tier}`;
}

function ConfigToggle(props: {
  checked: boolean;
  description?: string;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="group flex items-start gap-3 border-t border-ink/10 py-4 text-sm first:border-t-0">
      <input
        checked={props.checked}
        className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.currentTarget.checked)}
        type="checkbox"
      />
      <span className="space-y-1">
        <span className="block font-medium text-foreground group-has-[:disabled]:text-muted-foreground">
          {props.label}
        </span>
        {props.description ? (
          <span className="block text-muted-foreground">{props.description}</span>
        ) : null}
      </span>
    </label>
  );
}

export function ExportSettingsStep({
  capabilities,
  config,
  defaultAuthorName,
  defaultBookTitle,
  metadataStatus,
  onConfigChange,
  onSaveBookMetadata,
  onSettingsChange,
  settings,
  summary,
}: ExportSettingsStepProps) {
  const { t } = useTranslation();
  const appendicesLocked = capabilities.allowAppendices === false || config.exportMode !== "full_compendium";
  const printControlsLocked = capabilities.allowPrintMode === false || config.printMode === false;
  const lockedHint = (requiredTier: BookExportCapabilityMatrix["tier"]) =>
    t("bookAssembly.exportSettings.lockedHint", {
      tier: t(getTierLabelKey(requiredTier)),
    });
  const appendixHint = !capabilities.allowAppendices
    ? lockedHint(capabilities.appendixRequiredTier)
    : config.exportMode !== "full_compendium"
      ? t("bookAssembly.exportSettings.appendixModeHint")
      : t("bookAssembly.exportSettings.appendixBody");
  const illustrationHint = capabilities.allowIllustrations
    ? t("bookAssembly.exportSettings.toggleHelp.includeIllustrations")
    : lockedHint(capabilities.illustrationsRequiredTier);
  const printModeHint = capabilities.allowPrintMode
    ? t("bookAssembly.exportSettings.toggleHelp.printMode")
    : lockedHint(capabilities.printModeRequiredTier);
  const mirroredMarginsHint = !capabilities.allowMirroredMargins
    ? lockedHint(capabilities.mirroredMarginsRequiredTier)
    : !config.printMode
      ? t("bookAssembly.exportSettings.printModeHint")
      : t("bookAssembly.exportSettings.toggleHelp.mirroredMargins");
  const rightPageStartsHint = !capabilities.allowRightPageStarts
    ? lockedHint(capabilities.rightPageStartsRequiredTier)
    : !config.printMode
      ? t("bookAssembly.exportSettings.printModeHint")
      : t("bookAssembly.exportSettings.toggleHelp.startChaptersOnRightPage");

  return (
    <div className="grid gap-8 xl:grid-cols-[minmax(0,1.35fr)_minmax(24rem,0.9fr)]">
      <section className="space-y-8">
        <div className="space-y-8">
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-clay">
                  {t("bookAssembly.steps.exportSettings.eyebrow")}
                </p>
                <h2 className="text-3xl text-foreground">
                  {t("bookAssembly.steps.exportSettings.title")}
                </h2>
                <p className="max-w-3xl text-sm leading-7 text-muted-foreground">
                  {t("bookAssembly.steps.exportSettings.body")}
                </p>
              </div>
              <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm sm:flex">
                <LayoutTemplate className="h-5 w-5" />
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <section className="grid gap-4 border-t border-ink/10 pt-8 lg:grid-cols-[12rem_1fr]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {t("bookAssembly.exportSettings.targetLabel")}
                </p>
              </div>
              <div className="grid gap-2 md:grid-cols-3">
                {bookExportTargets.map((target) => {
                  const allowed = capabilities.allowedTargets.includes(target);
                  const requiredTier = capabilities.targetRequirements[target];

                  return (
                    <button
                      className={`rounded-[14px] border px-4 py-4 text-left transition-colors ${
                        config.target === target
                          ? "border-primary bg-primary text-primary-foreground shadow-sm"
                          : "border-ink/10 bg-white/55 hover:border-primary/40 hover:bg-white/80"
                      } ${allowed ? "" : "cursor-not-allowed text-muted-foreground/80"}`}
                      data-testid={`book-export-target-${target}`}
                      disabled={!allowed}
                      key={target}
                      onClick={() => onConfigChange({ target })}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium">{t(getTargetLabelKey(target))}</p>
                        {!allowed ? <LockKeyhole className="h-4 w-4" /> : null}
                      </div>
                      <p className={`mt-2 text-sm ${config.target === target ? "text-primary-foreground/75" : "text-muted-foreground"}`}>
                        {allowed
                          ? t("bookAssembly.exportSettings.targetHelp.available")
                          : lockedHint(requiredTier)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="grid gap-4 border-t border-ink/10 pt-8 lg:grid-cols-[12rem_1fr]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {t("bookAssembly.exportSettings.modeLabel")}
              </p>
              <div className="grid gap-2 md:grid-cols-3">
                {bookExportModes.map((mode) => {
                  const allowed = capabilities.allowedModes.includes(mode);
                  const requiredTier = capabilities.modeRequirements[mode];

                  return (
                    <button
                      className={`rounded-[14px] border px-4 py-4 text-left transition-colors ${
                        config.exportMode === mode
                          ? "border-primary bg-primary text-primary-foreground shadow-sm"
                          : "border-ink/10 bg-white/55 hover:border-primary/40 hover:bg-white/80"
                      } ${allowed ? "" : "cursor-not-allowed text-muted-foreground/80"}`}
                      disabled={!allowed}
                      data-testid={`book-export-mode-${mode}`}
                      key={mode}
                      onClick={() => onConfigChange({ exportMode: mode })}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium">{t(getModeLabelKey(mode))}</p>
                        {!allowed ? <LockKeyhole className="h-4 w-4" /> : null}
                      </div>
                      <p className={`mt-2 text-sm ${config.exportMode === mode ? "text-primary-foreground/75" : "text-muted-foreground"}`}>
                        {allowed
                          ? t(`bookAssembly.exportSettings.modeHelp.${mode}`)
                          : lockedHint(requiredTier)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="grid gap-4 border-t border-ink/10 pt-8 lg:grid-cols-[12rem_1fr]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {t("bookAssembly.exportSettings.presetLabel")}
              </p>
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {bookExportPresets.map((preset) => {
                  const allowed = capabilities.allowedPresets.includes(preset);
                  const requiredTier = capabilities.presetRequirements[preset];

                  return (
                    <button
                      className={`rounded-[14px] border px-4 py-4 text-left transition-colors ${
                        config.preset === preset
                          ? "border-primary bg-primary text-primary-foreground shadow-sm"
                          : "border-ink/10 bg-white/55 hover:border-primary/40 hover:bg-white/80"
                      } ${allowed ? "" : "cursor-not-allowed text-muted-foreground/80"}`}
                      disabled={!allowed}
                      data-testid={`book-export-preset-${preset}`}
                      key={preset}
                      onClick={() => onConfigChange({ preset })}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium">{t(getPresetLabelKey(preset))}</p>
                        {!allowed ? <LockKeyhole className="h-4 w-4" /> : null}
                      </div>
                      <p className={`mt-2 text-sm ${config.preset === preset ? "text-primary-foreground/75" : "text-muted-foreground"}`}>
                        {allowed
                          ? t(`bookAssembly.exportSettings.presetHelp.${preset}`)
                          : lockedHint(requiredTier)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="space-y-5 border-t border-ink/10 pt-8">
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t("bookAssembly.exportSettings.pageSizeLabel")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {bookExportPageSizes.map((pageSize) => (
                      <Button
                        key={pageSize}
                        onClick={() => onConfigChange({ pageSize })}
                        type="button"
                        variant={config.pageSize === pageSize ? "default" : "outline"}
                      >
                        {t(getPageSizeLabelKey(pageSize))}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t("bookAssembly.exportSettings.messageSourcesLabel")}
                  </p>
                  <div className="grid gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="front-matter-source">
                        {t("bookAssembly.exportSettings.frontMatterLabel")}
                      </Label>
                      <Select
                        onValueChange={(frontMatterMessageSource) =>
                          onConfigChange({
                            frontMatterMessageSource:
                              frontMatterMessageSource as BookExportConfig["frontMatterMessageSource"],
                          })}
                        value={config.frontMatterMessageSource}
                      >
                        <SelectTrigger id="front-matter-source">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(["none", "author_note", "dedication"] as const).map((source) => (
                            <SelectItem key={source} value={source}>
                              {t(getFrontMatterSourceLabelKey(source))}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="end-matter-source">
                        {t("bookAssembly.exportSettings.endMatterLabel")}
                      </Label>
                      <Select
                        onValueChange={(endMatterMessageSource) =>
                          onConfigChange({
                            endMatterMessageSource:
                              endMatterMessageSource as BookExportConfig["endMatterMessageSource"],
                          })}
                        value={config.endMatterMessageSource}
                      >
                        <SelectTrigger id="end-matter-source">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(["none", "author_note", "special_thanks"] as const).map((source) => (
                            <SelectItem key={source} value={source}>
                              {t(getEndMatterSourceLabelKey(source))}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-x-8 md:grid-cols-2">
                <ConfigToggle
                  checked={config.includeCover}
                  description={t("bookAssembly.exportSettings.toggleHelp.includeCover")}
                  label={t("bookAssembly.exportSettings.toggleLabels.includeCover")}
                  onChange={(includeCover) => onConfigChange({ includeCover })}
                />
                <ConfigToggle
                  checked={config.includeTitlePage}
                  description={t("bookAssembly.exportSettings.toggleHelp.includeTitlePage")}
                  label={t("bookAssembly.exportSettings.toggleLabels.includeTitlePage")}
                  onChange={(includeTitlePage) => onConfigChange({ includeTitlePage })}
                />
                <ConfigToggle
                  checked={config.includeToc}
                  description={t("bookAssembly.exportSettings.toggleHelp.includeToc")}
                  label={t("bookAssembly.exportSettings.toggleLabels.includeToc")}
                  onChange={(includeToc) => onConfigChange({ includeToc })}
                />
                <ConfigToggle
                  checked={config.showPageNumbers}
                  description={t("bookAssembly.exportSettings.toggleHelp.showPageNumbers")}
                  label={t("bookAssembly.exportSettings.toggleLabels.showPageNumbers")}
                  onChange={(showPageNumbers) => onConfigChange({ showPageNumbers })}
                />
                <ConfigToggle
                  checked={config.includeIllustrations}
                  description={illustrationHint}
                  disabled={!capabilities.allowIllustrations}
                  label={t("bookAssembly.exportSettings.toggleLabels.includeIllustrations")}
                  onChange={(includeIllustrations) => onConfigChange({ includeIllustrations })}
                />
                <ConfigToggle
                  checked={config.printMode}
                  description={printModeHint}
                  disabled={!capabilities.allowPrintMode}
                  label={t("bookAssembly.exportSettings.toggleLabels.printMode")}
                  onChange={(printMode) => onConfigChange({ printMode })}
                />
                <ConfigToggle
                  checked={config.mirroredMargins}
                  description={mirroredMarginsHint}
                  disabled={!capabilities.allowMirroredMargins || printControlsLocked}
                  label={t("bookAssembly.exportSettings.toggleLabels.mirroredMargins")}
                  onChange={(mirroredMargins) => onConfigChange({ mirroredMargins })}
                />
                <ConfigToggle
                  checked={config.startChaptersOnRightPage}
                  description={rightPageStartsHint}
                  disabled={!capabilities.allowRightPageStarts || printControlsLocked}
                  label={t("bookAssembly.exportSettings.toggleLabels.startChaptersOnRightPage")}
                  onChange={(startChaptersOnRightPage) => onConfigChange({ startChaptersOnRightPage })}
                />
              </div>
            </section>

            <section className="space-y-4 border-t border-ink/10 pt-8">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t("bookAssembly.exportSettings.appendixLabel")}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {appendixHint}
                  </p>
                </div>
                {appendicesLocked ? <LockKeyhole className="h-4 w-4 text-muted-foreground" /> : null}
              </div>
              <div className="grid gap-x-8 md:grid-cols-3">
                <ConfigToggle
                  checked={config.includeCharacterAppendix}
                  disabled={appendicesLocked}
                  label={t("bookAssembly.exportSettings.toggleLabels.includeCharacterAppendix")}
                  onChange={(includeCharacterAppendix) => onConfigChange({ includeCharacterAppendix })}
                />
                <ConfigToggle
                  checked={config.includeLoreAppendix}
                  disabled={appendicesLocked}
                  label={t("bookAssembly.exportSettings.toggleLabels.includeLoreAppendix")}
                  onChange={(includeLoreAppendix) => onConfigChange({ includeLoreAppendix })}
                />
                <ConfigToggle
                  checked={config.includeTimelineAppendix}
                  disabled={appendicesLocked}
                  label={t("bookAssembly.exportSettings.toggleLabels.includeTimelineAppendix")}
                  onChange={(includeTimelineAppendix) => onConfigChange({ includeTimelineAppendix })}
                />
              </div>
            </section>
          </div>
        </div>
      </section>

      <aside className="space-y-6 xl:sticky xl:top-6 xl:self-start">
        <BookExportSummaryCard summary={summary} />
        <BookExportBookMetadataForm
          defaultAuthorName={defaultAuthorName}
          defaultBookTitle={defaultBookTitle}
          onSave={onSaveBookMetadata}
          onSettingsChange={onSettingsChange}
          saveStatus={metadataStatus}
          settings={settings}
        />
      </aside>
    </div>
  );
}
